import { axiosInstance } from '../App';

const BASE = '/api/secured/study-results';

// ── Types ────────────────────────────────────────────────────────────

export interface StudyListRequest {
  studyTypeId: number;
  offset: number;
  pageSize: number;
  stateFilter?: number[];
  sortField?: string;
  sortOrder?: number;
  filters?: Record<string, string>;
}

export interface StudyListItem {
  healthPatientServiceId: number;
  externalId: string | null;
  presumptiveDiagnostic: string | null;
  studyTypeId: number;
  studyOrderId: number | null;
  clinicHistoryId: string;
  patientId: number;
  patientName: string;
  age: number | null;
  serviceDate: string;
  stateTypeId: number;
  stateTypeDesc: string;
  orderedBy: string | null;
  orderDetail: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  offset: number;
  pageSize: number;
}

export interface StateCount {
  stateTypeId: number;
  stateTypeDesc: string;
  count: number;
}

export interface StudyTypeOption {
  id: number;
  name: string;
  iconSmall: string | null;
}

export interface StudyStateTypeOption {
  id: number;
  name: string;
}

export interface ProfessionalOption {
  id: number;
  name: string;
}

export interface DeterminationOption {
  id: number;
  description: string;
  numeric: boolean;
  unit: string | null;
}

export interface ExistingFile {
  fileId: number;
  fileName: string;
  description: string | null;
}

export interface StudyDetail {
  healthPatientServiceId: number;
  patientId: number;
  studyTypeId: number | null;
  studyTypeName: string | null;
  serviceDate: string | null;
  orderedById: number | null;
  orderedByName: string | null;
  externalId: string | null;
  presumptiveDiagnostic: string | null;
  stateTypeId: number;
  resultDate: string | null;
  resultDescription: string | null;
  resultText: string | null;
  existingFiles: ExistingFile[];
  studyOrderId: number | null;
  studyOrderProfessional: string | null;
  studyOrderDate: string | null;
  studyOrderDiagnosis: string | null;
  studyOrderItems: string | null;
}

export interface StudyOrderInfo {
  studyOrderId: number;
  studyTypeId: number | null;
  studyTypeName: string | null;
  presumptiveDiagnostic: string | null;
  studyOrderItems: string | null;
}

export interface UploadedFile {
  tempFileId: string;
  fileName: string;
  size: number;
}

export interface DeterminationValue {
  determinationId: number;
  value: string;
}

export interface SaveStudyResultRequest {
  healthPatientServiceId: number;
  patientId: number;
  studyTypeId: number;
  studyOrderId?: number | null;
  serviceDate: string;
  orderedById: number | null;
  externalId: string | null;
  presumptiveDiagnostic: string | null;
  stateTypeId: number;
  resultDate: string;
  resultDescription: string;
  resultText: string | null;
  tempFileIds: string[];
  determinations: DeterminationValue[];
}

export interface SaveStudyResultResponse {
  healthPatientServiceId: number;
  success: boolean;
  message: string | null;
}

// ── API calls ────────────────────────────────────────────────────────

export async function listStudiesForResult(
  request: StudyListRequest,
): Promise<PaginatedResponse<StudyListItem>> {
  const { data } = await axiosInstance.post<PaginatedResponse<StudyListItem>>(
    `${BASE}/list`,
    request,
  );
  return data;
}

export async function getStateCounts(
  studyTypeId: number,
): Promise<StateCount[]> {
  const { data } = await axiosInstance.get<StateCount[]>(
    `${BASE}/state-counts/${studyTypeId}`,
  );
  return data;
}

export async function getStudyDetail(
  hpsId: number,
): Promise<StudyDetail> {
  const { data } = await axiosInstance.get<StudyDetail>(`${BASE}/${hpsId}`);
  return data;
}

export async function getStudyOrderInfo(
  studyOrderId: number,
): Promise<StudyOrderInfo> {
  const { data } = await axiosInstance.get<StudyOrderInfo>(
    `${BASE}/study-order-info/${studyOrderId}`,
  );
  return data;
}

export async function getStudyTypes(): Promise<StudyTypeOption[]> {
  const { data } = await axiosInstance.get<StudyTypeOption[]>(
    `${BASE}/study-types`,
  );
  return data;
}

export async function getStudyStateTypes(): Promise<StudyStateTypeOption[]> {
  const { data } = await axiosInstance.get<StudyStateTypeOption[]>(
    `${BASE}/study-state-types`,
  );
  return data;
}

export async function searchProfessionals(
  filter: string,
): Promise<ProfessionalOption[]> {
  const { data } = await axiosInstance.get<ProfessionalOption[]>(
    `${BASE}/professionals`,
    { params: { filter } },
  );
  return data;
}

export async function searchDeterminations(
  filter: string,
): Promise<DeterminationOption[]> {
  const { data } = await axiosInstance.get<DeterminationOption[]>(
    `${BASE}/determinations`,
    { params: { filter } },
  );
  return data;
}

export async function uploadStudyFile(file: File): Promise<UploadedFile> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axiosInstance.post<UploadedFile>(
    `${BASE}/upload-file`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function deleteStudyUploadedFile(
  tempFileId: string,
): Promise<void> {
  await axiosInstance.delete(`${BASE}/uploaded-file/${tempFileId}`);
}

export async function saveStudyResult(
  request: SaveStudyResultRequest,
): Promise<SaveStudyResultResponse> {
  const { data } = await axiosInstance.post<SaveStudyResultResponse>(
    `${BASE}/save`,
    request,
  );
  return data;
}

export async function printStudyOrder(hpsId: number): Promise<Blob> {
  const response = await axiosInstance.get(`${BASE}/print-order/${hpsId}`, {
    responseType: 'blob',
  });
  return response.data;
}

// ── Helpers ──────────────────────────────────────────────────────────

export function getErrorMessage(error: unknown, fallback: string): string {
  const candidate = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return (
    candidate?.response?.data?.message || candidate?.message || fallback
  );
}
